import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => {
    // Default to URL language if present, otherwise localStorage or 'en'
    if (typeof window !== "undefined") {
      const urlLang = window.location.pathname.startsWith("/ar") ? "ar" : "en";
      return urlLang || localStorage.getItem("selectedLanguage") || "en";
    }
    return localStorage.getItem("selectedLanguage") || "en";
  });

  const getLanguageFromPath = (pathname) =>
    pathname.startsWith("/ar") ? "ar" : "en";

  const stripLanguagePrefix = (pathname) => {
    if (pathname === "/ar") {
      return "/";
    }
    return pathname.replace(/^\/ar(?=\/|$)/, "") || "/";
  };

  const buildLocalizedPath = (pathname, search = "", hash = "", lang) => {
    const basePath = stripLanguagePrefix(pathname);
    const targetLang = lang || language;
    if (targetLang === "ar") {
      const prefixedPath = basePath === "/" ? "/ar" : `/ar${basePath}`;
      return `${prefixedPath}${search}${hash}`;
    }
    return `${basePath}${search}${hash}`;
  };

  const getLocalizedPath = (to, langOverride) => {
    let path = to || "/";
    let search = "";
    let hash = "";

    const hashIndex = path.indexOf("#");
    if (hashIndex >= 0) {
      hash = path.slice(hashIndex);
      path = path.slice(0, hashIndex);
    }

    const searchIndex = path.indexOf("?");
    if (searchIndex >= 0) {
      search = path.slice(searchIndex);
      path = path.slice(0, searchIndex);
    }

    if (!path.startsWith("/")) {
      path = `/${path}`;
    }

    return buildLocalizedPath(path, search, hash, langOverride);
  };

  useEffect(() => {
    // Update language based on URL (URL is the source of truth)
    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang !== language) {
      setLanguage(urlLang);
    }
  }, [location.pathname]);

  useEffect(() => {
    // Save language to localStorage whenever it changes
    localStorage.setItem("selectedLanguage", language);

    // Update document direction based on language
    document.documentElement.setAttribute(
      "dir",
      language === "ar" ? "rtl" : "ltr"
    );
    document.documentElement.setAttribute("lang", language);

    // Ensure URL matches current language
    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang !== language) {
      const nextPath = buildLocalizedPath(
        location.pathname,
        location.search,
        location.hash,
        language
      );
      navigate(nextPath, { replace: true });
    }

    // Scroll to top when language changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [language, location.pathname, location.search, location.hash, navigate]);

  const toggleLanguage = () => {
    setLanguage((prevLang) => (prevLang === "en" ? "ar" : "en"));
  };

  const setLanguageTo = (lang) => {
    if (lang === "en" || lang === "ar") {
      setLanguage(lang);
    }
  };

  const isArabic = language === "ar";
  const isEnglish = language === "en";

  const value = {
    language,
    setLanguage: setLanguageTo,
    toggleLanguage,
    isArabic,
    isEnglish,
    getLocalizedPath,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
